import type { HTMLAttributes, PropsWithChildren } from 'react';
import { Children } from 'react';

import { css } from '@compiled/react';

import { generateSafeIds } from '@/libraries/design/utils';

import { TabFC, mapChild } from './tab-helper';
import { useTabsContext } from './tabs';

export type TabPanelProps = PropsWithChildren<{
	forceRender?: boolean;
	panelIndex?: number;
	hideTabPanelRole?: boolean;
}> &
	HTMLAttributes<HTMLDivElement>;

const hidePanelStyle = css({
	display: 'none',
});

const TabPanel: TabFC<TabPanelProps> = ({
	forceRender,
	children,
	panelIndex,
	id,
	hideTabPanelRole,
	...props
}: TabPanelProps) => {
	const { isCurrentTab } = useTabsContext();

	const isTabSelected = isCurrentTab(panelIndex);
	const activeClass = isTabSelected ? 'active-panel' : '';

	const { panelId, tabId } = generateSafeIds(id, panelIndex);

	const originalChildrenCount = Children.count(children);
	const shouldHideRole =
		hideTabPanelRole !== undefined ? hideTabPanelRole : originalChildrenCount === 0;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { css: existingCss, ...propsWithoutCss } = props;

	const filteredProps = Object.fromEntries(
		Object.entries(propsWithoutCss).filter(([key]) => !key.startsWith('aria-') && key !== 'role'),
	) as typeof propsWithoutCss;

	const inlineHideStyle = !isTabSelected && !forceRender ? { display: 'none' } : undefined;

	if (!shouldHideRole) {
		return (
			<div
				{...filteredProps}
				css={[!isTabSelected && !forceRender && hidePanelStyle]}
				style={inlineHideStyle}
				id={panelId}
				data-testid={panelId}
				aria-labelledby={tabId}
				aria-hidden={!isTabSelected}
				role="tabpanel"
				className={['tab-panel', activeClass].join(' ')}
			>
				{Children.map(children, (child) => {
					return mapChild(child, { isPanelActive: isTabSelected });
				})}
			</div>
		);
	}

	return (
		<div
			css={[!isTabSelected && !forceRender && hidePanelStyle]}
			style={inlineHideStyle}
			id={panelId}
			data-testid={panelId}
			className={['tab-panel', activeClass].join(' ')}
			aria-labelledby={undefined}
			aria-hidden={undefined}
			role={undefined}
		>
			{Children.map(children, (child) => {
				return mapChild(child, { isPanelActive: isTabSelected });
			})}
		</div>
	);
};
TabPanel.tabsRole = 'TabPanel';

export { TabPanel };
